import { ExecutionResult } from 'graphql';
import _ from 'lodash';

import { JSONObject } from 'src/app/graph/explorerPage/helpers/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import {
  EXPLORER_REQUEST,
  EXPLORER_RESPONSE,
  IncomingEmbedMessageEvent,
  sendPostMessageFromEmbedToParent,
} from '../../helpers/postMessageHelpers';

import {
  DEFAULT_CURRENT_RESPONSE,
  OnResponseUpdate,
  PerformOperationReturn,
  ResponseAndMetrics,
  handleApolloHeadersAndExtraction,
  performOperation,
  updateMultipartResponse,
} from './performOperation/performOperation';
import { FileVariable } from './useEditorTabState/useEditorTabState';

function readFile(file: File): Promise<ArrayBuffer | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        reject(new Error('invalid data returned'));
      } else {
        resolve(reader.result);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function isNonNullArrayBuffer<Data extends { arrayBuffer: ArrayBuffer | null }>(
  value: Data,
): value is Data & { arrayBuffer: ArrayBuffer } {
  return value.arrayBuffer !== null;
}

export const performPostMessageOperation = async <
  TOnResponseUpdate extends OnResponseUpdate,
>({
  operationName,
  operationString,
  variables,
  processedHeaders,
  includeCookies,
  endpointUrl,
  onResponseUpdate,
  requestQueryPlan,
  showTraces,
  fileVariables: inputtedFileVariables,
  requestConnectorsDebugging,
}: {
  operationName: string | undefined;
  operationString: string;
  variables: JSONObject;
  processedHeaders: Record<string, string>;
  includeCookies: boolean;
  // This is only here to be backwards compatible with old versions of the embed for Sandbox.
  // We used to only send through the endpoint for Sandbox, but now for any graphRef
  // the endpoint we query on the parent page is the endpoint registered on Studio.
  // For manual schema embeds & dev tools, this endpointUrl is ignored and the endpoint in the config is used.
  endpointUrl: string | null;
  onResponseUpdate: TOnResponseUpdate;
  requestQueryPlan: boolean;
  requestConnectorsDebugging: boolean;
  showTraces: boolean;
  fileVariables: FileVariable[] | undefined;
}): ReturnType<typeof performOperation<TOnResponseUpdate>> => {
  const stableOperationId = _.uniqueId();

  const startTime = Date.now();

  // convert file variables to bytes
  const fileVariables = await Promise.all(
    (inputtedFileVariables ?? []).map(async (fileVariable) => {
      const files: { arrayBuffer: ArrayBuffer; fileName: string }[] = (
        await Promise.all(
          Array.from(fileVariable.files ?? []).map(async (file) => {
            const result = await readFile(file);
            return { arrayBuffer: result, fileName: file.name };
          }),
        )
      ).filter(isNonNullArrayBuffer);
      return {
        ...fileVariable,
        files,
      };
    }),
  );
  const { processedHeadersWithApolloHeaders, extractExtensions } =
    handleApolloHeadersAndExtraction({
      processedHeaders,
      operationString,
      requestQueryPlan,
      requestConnectorsDebugging,
      showTraces,
      isMultipartSubscription: false,
      // for multipart form requests, the content type needs to have `multipart/form-data`
      // and the boundary https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Type
      // the browser fills it in for us when we make it undefined,
      // since we don't know the boundary at this stage
      addContentType: !isMinLengthArray(1, fileVariables),
    });

  sendPostMessageFromEmbedToParent(
    {
      name: EXPLORER_REQUEST,
      operationId: stableOperationId,
      operation: operationString,
      variables,
      fileVariables,
      operationName,
      includeCookies,
      headers: processedHeadersWithApolloHeaders,
      sandboxEndpointUrl: endpointUrl ?? undefined,
      endpointUrl: endpointUrl ?? undefined,
    },
    fileVariables.flatMap(({ files }) =>
      files.map(({ arrayBuffer }) => arrayBuffer),
    ),
  );

  // Listen to parent for response and dispose event listener when message received
  return new Promise((resolve) => {
    let currentResponse = _.cloneDeep(DEFAULT_CURRENT_RESPONSE);
    let hasResolved = false;
    let isFirst = true;

    const handlePostMessageResponse = (event: IncomingEmbedMessageEvent) => {
      if (
        event.data.name === EXPLORER_RESPONSE &&
        event.data.operationId === stableOperationId
      ) {
        const endTime = Date.now();
        // TODO: right now these three keys are merged with the response, so we have to strip them all before returning
        // we should update the embeds to send an unadulterated response
        const { headers, status, hasNext, ...newResponse } =
          event.data.response;
        const headersArray: MinLengthArray<
          1,
          Record<string, string>
        > = Array.isArray(headers) ? headers : [headers || {}];

        const size = headersArray[0]?.['content-length']
          ? parseInt(headersArray[0]['content-length'], 10)
          : (newResponse.size ?? 0);

        if (isFirst && !event.data.response.hasNext) {
          //  Not a multipart response, change body as little as possible
          const body: ExecutionResult<{}> = newResponse;

          const extensions = (body && extractExtensions({ body })) ?? {};

          const reponseAndMetrics: ResponseAndMetrics = {
            type: 'result',
            body,
            headers: headersArray,
            status: status ?? 0,
            size,
            hasNext: false,
            startTime,
            endTime,
            ...extensions,
            get bodyText() {
              return JSON.stringify(body);
            },
          };

          if (onResponseUpdate) {
            onResponseUpdate(reponseAndMetrics, true);
            resolve({
              type: 'response-updated-in-callback',
              status,
            } as PerformOperationReturn<TOnResponseUpdate>);
          } else {
            resolve(
              reponseAndMetrics as PerformOperationReturn<TOnResponseUpdate>,
            );
          }
        } else {
          currentResponse = updateMultipartResponse({
            currentResponse,
            newResponse: {
              headers,
              status,
              data: newResponse,
              size: size ?? newResponse.size ?? 0,
            },
            extractExtensions,
            // only used in subscriptions
            shouldTerminate: false,
          });

          const updatedResponse: ResponseAndMetrics = {
            type: 'result',
            bodyText: JSON.stringify(currentResponse.body),
            status: status ?? 0,
            hasNext: hasNext ?? false,
            startTime,
            endTime,
            ...currentResponse,
          };

          if (!hasResolved && (onResponseUpdate || !hasNext)) {
            hasResolved = true;
            if (!hasNext) {
              window.removeEventListener('message', handlePostMessageResponse);
            }
            if (onResponseUpdate) {
              onResponseUpdate(updatedResponse, false);
              resolve({
                type: 'response-updated-in-callback',
                status: updatedResponse.status,
              } as PerformOperationReturn<TOnResponseUpdate>);
            } else {
              resolve(
                updatedResponse as PerformOperationReturn<TOnResponseUpdate>,
              );
            }
          } else {
            onResponseUpdate?.(updatedResponse, false);
          }
        }
        isFirst = false;
      }
    };

    window.addEventListener('message', handlePostMessageResponse);
  });
};
